import {
  AdminSubscriptionPlan,
  PlanCurrencyCode,
  PlanDetailResponse,
  PlanDetailUpdate,
  PlanDetailVariant,
  PlanRegionalPriceMap,
  RegionalPrice,
  UpdatePlanRequest,
} from '../../types/api';
import { PLAN_FEATURE_CONFIG, PlanFeatureKey } from './constants/planFeatures';
import {
  CUSTOM_PLAN_NAME,
  EditSubscriptionFormData,
  EditSubscriptionVariantMeta,
  PLAN_CURRENCIES,
  PlanFeatureItem,
  PricingRow,
  RegionalPriceCell,
  SubscriptionPlanView,
} from './types/subscription';

const emptyRegionalCell = (): RegionalPriceCell => ({ amount: 0, taxPercent: 0 });

export const emptyRegionalPriceMap = (): PlanRegionalPriceMap => ({
  INR: emptyRegionalCell(),
  USD: emptyRegionalCell(),
  CAD: emptyRegionalCell(),
  EUR: emptyRegionalCell(),
});

const cloneRegionalPriceMap = (map: PlanRegionalPriceMap): PlanRegionalPriceMap => ({
  INR: { ...map.INR },
  USD: { ...map.USD },
  CAD: { ...map.CAD },
  EUR: { ...map.EUR },
});

const toPriceCell = (input: unknown): RegionalPriceCell => {
  if (typeof input === 'number') {
    return { amount: input, taxPercent: 0 };
  }

  if (input && typeof input === 'object') {
    const value = input as {
      amount?: unknown;
      taxPercent?: unknown;
      tax_percent?: unknown;
    };

    const amount =
      typeof value.amount === 'number' && Number.isFinite(value.amount) ? value.amount : 0;
    const taxRaw = value.taxPercent ?? value.tax_percent;
    const taxPercent =
      typeof taxRaw === 'number' && Number.isFinite(taxRaw) ? taxRaw : 0;

    return { amount, taxPercent };
  }

  return emptyRegionalCell();
};

const pickRegionValue = (source: Record<string, unknown>, keys: string[]): unknown => {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return undefined;
};

export const mapApiPriceToRegionalMap = (price?: Record<string, unknown>): PlanRegionalPriceMap => {
  const source = price ?? {};

  return {
    INR: toPriceCell(pickRegionValue(source, ['India', 'IN', '_i_n', 'INR', 'india', 'in'])),
    USD: toPriceCell(pickRegionValue(source, ['US', '_u_s', 'USD', 'us'])),
    CAD: toPriceCell(pickRegionValue(source, ['Canada', 'CA', '_c_a', 'CAD', 'canada', 'ca'])),
    EUR: toPriceCell(pickRegionValue(source, ['EU', '_e_u', 'EUR', 'eu'])),
  };
};

export const mapRegionalMapToApiPrice = (price: PlanRegionalPriceMap): RegionalPrice => ({
  _i_n: { amount: price.INR.amount, taxPercent: price.INR.taxPercent },
  _u_s: { amount: price.USD.amount, taxPercent: price.USD.taxPercent },
  _c_a: { amount: price.CAD.amount, taxPercent: price.CAD.taxPercent },
  _e_u: { amount: price.EUR.amount, taxPercent: price.EUR.taxPercent },
});

const isMonthlyVariant = (durationMonths: number) => durationMonths === 1;
const isYearlyVariant = (durationMonths: number) => durationMonths >= 12;

export const getPricingRowLabel = (durationMonths: number): string => {
  if (durationMonths === 1) return 'Monthly';
  if (durationMonths === 3) return '3 Months';
  if (durationMonths === 6) return '6 Months';
  if (durationMonths >= 12) return 'Yearly';
  return `${durationMonths} Months`;
};

export const buildPricingGrid = (
  monthlyPrice: PlanRegionalPriceMap | null,
  yearlyPrice: PlanRegionalPriceMap | null
): PricingRow[] => {
  const rows: PricingRow[] = [];

  if (monthlyPrice) {
    rows.push({
      planDetailId: 0,
      durationMonths: 1,
      label: 'Monthly',
      ...cloneRegionalPriceMap(monthlyPrice),
    });
  }

  if (yearlyPrice) {
    rows.push({
      planDetailId: 0,
      durationMonths: 12,
      label: 'Yearly',
      ...cloneRegionalPriceMap(yearlyPrice),
    });
  }

  return rows;
};

export const buildPricingGridFromVariants = (variants: PlanDetailVariant[]): PricingRow[] =>
  [...variants]
    .sort((a, b) => a.durationMonths - b.durationMonths)
    .map((variant) => {
      const planDetailId =
        typeof variant.planDetailId === 'number'
          ? variant.planDetailId
          : Number.parseInt(String(variant.planDetailId), 10);

      return {
        planDetailId,
        durationMonths: variant.durationMonths,
        label: getPricingRowLabel(variant.durationMonths),
        ...cloneRegionalPriceMap(mapApiPriceToRegionalMap(variant.price)),
      };
    });

const mapVariantToMeta = (variant: PlanDetailVariant): EditSubscriptionVariantMeta => {
  const planDetailId =
    typeof variant.planDetailId === 'number'
      ? Math.trunc(variant.planDetailId)
      : Number.parseInt(String(variant.planDetailId), 10);

  return {
    planDetailId,
    durationMonths: variant.durationMonths,
    connects: variant.connects ?? 0,
    advanceSearch: variant.advanceSearch ?? false,
    limitedSupport: variant.limitedSupport ?? false,
    limitedChat: variant.limitedChat ?? false,
    limitedChatDays: variant.limitedChatDays ?? 0,
    highlightedProfile: variant.highlightedProfile ?? false,
    astroMatchmaking: variant.astroMatchmaking ?? false,
    dedicatedManager: variant.dedicatedManager ?? false,
    dedicatedSeniorManager: variant.dedicatedSeniorManager ?? false,
    arrangedMeetings: variant.arrangedMeetings ?? false,
    hideProfile: variant.hideProfile ?? false,
  };
};

const getFeatureSourceVariant = (variants: PlanDetailVariant[]): PlanDetailVariant | undefined =>
  variants.find((variant) => variant.durationMonths === 1) ?? variants[0];

export const buildFeaturesFromVariantMeta = (
  variant: EditSubscriptionVariantMeta
): PlanFeatureItem[] => {
  const items: PlanFeatureItem[] = [
    {
      id: 'connects',
      key: 'connects',
      label: `${variant.connects} Connects`,
      enabled: variant.connects > 0,
    },
  ];

  PLAN_FEATURE_CONFIG.forEach((config) => {
    items.push({
      id: config.key,
      key: config.key,
      label: config.label,
      enabled: Boolean(variant[config.key as PlanFeatureKey]),
    });
  });

  return items;
};

export const buildFeatureLabelsFromVariantMeta = (
  variant: EditSubscriptionVariantMeta
): string[] =>
  buildFeaturesFromVariantMeta(variant)
    .filter((feature) => feature.enabled)
    .map((feature) => feature.label);

export const buildFeaturesFromVariants = (variants: PlanDetailVariant[]): PlanFeatureItem[] => {
  const sourceVariant = getFeatureSourceVariant(variants);
  if (!sourceVariant) return [];
  return buildFeaturesFromVariantMeta(mapVariantToMeta(sourceVariant));
};

export const buildFeatureLabelsFromVariants = (variants: PlanDetailVariant[]): string[] =>
  buildFeaturesFromVariants(variants)
    .filter((feature) => feature.enabled)
    .map((feature) => feature.label);

export const updateVariantFeatureFlag = (
  variant: EditSubscriptionVariantMeta,
  featureKey: string,
  enabled: boolean
): EditSubscriptionVariantMeta => {
  if (featureKey === 'connects') return variant;
  if (!(featureKey in variant)) return variant;
  return { ...variant, [featureKey]: enabled };
};

export const mapPlanDetailToSubscriptionView = (
  plan: PlanDetailResponse,
  subscribers = 0
): SubscriptionPlanView => {
  const variants = plan.variants ?? [];
  const monthlyVariant = variants.find((variant) => isMonthlyVariant(variant.durationMonths));
  const yearlyVariant = variants.find((variant) => isYearlyVariant(variant.durationMonths));

  return {
    id: plan.planId,
    name: plan.name,
    description: plan.description,
    monthlyPrice: monthlyVariant ? mapApiPriceToRegionalMap(monthlyVariant.price) : null,
    yearlyPrice: yearlyVariant ? mapApiPriceToRegionalMap(yearlyVariant.price) : null,
    pricingGrid: buildPricingGridFromVariants(variants),
    features: buildFeatureLabelsFromVariants(variants),
    subscribers: plan.activeUserCount ?? subscribers,
    isHidden: plan.isHidden,
    isPopular: plan.isPopular,
    isFree: plan.isFree,
    isDynamic: plan.isDynamic ?? false,
  };
};

const currencyFormatters: Record<PlanCurrencyCode, Intl.NumberFormat> = {
  INR: new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }),
  USD: new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }),
  CAD: new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }),
  EUR: new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }),
};

const numberFormatter = new Intl.NumberFormat('en-US');

export const formatRegionalAmount = (currency: PlanCurrencyCode, amount: number): string =>
  currencyFormatters[currency].format(amount);

export const formatTaxPercent = (taxPercent: number): string =>
  taxPercent > 0 ? `${taxPercent}% tax` : 'No tax';

export const formatSubscriberCount = (count: number): string => numberFormatter.format(count);

export const buildEditFormFromPlanDetail = (plan: PlanDetailResponse): EditSubscriptionFormData => {
  const variants = plan.variants ?? [];
  const isDynamic = plan.isDynamic ?? false;

  return {
    name: isDynamic ? CUSTOM_PLAN_NAME : plan.name,
    description: plan.description,
    pricingGrid: buildPricingGridFromVariants(variants),
    isPopular: plan.isPopular,
    isActive: isDynamic ? false : !plan.isHidden,
    isFree: isDynamic ? false : plan.isFree,
    isDynamic,
    variants: variants.map(mapVariantToMeta),
  };
};

export const buildEditFormFromPlan = (plan: SubscriptionPlanView): EditSubscriptionFormData => {
  const isDynamic = plan.isDynamic ?? false;

  return {
    name: isDynamic ? CUSTOM_PLAN_NAME : plan.name,
    description: plan.description,
    pricingGrid:
      plan.pricingGrid.length > 0
        ? plan.pricingGrid.map((row) => ({
            ...row,
            INR: { ...row.INR },
            USD: { ...row.USD },
            CAD: { ...row.CAD },
            EUR: { ...row.EUR },
          }))
        : buildPricingGrid(plan.monthlyPrice, plan.yearlyPrice),
    isPopular: plan.isPopular,
    isActive: isDynamic ? false : !plan.isHidden,
    isFree: isDynamic ? false : plan.isFree,
    isDynamic,
    variants: [],
  };
};

const pricingRowToRegionalMap = (row: PricingRow): PlanRegionalPriceMap => ({
  INR: { ...row.INR },
  USD: { ...row.USD },
  CAD: { ...row.CAD },
  EUR: { ...row.EUR },
});

const toPlanDetailId = (value: number | string): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }

  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  throw new Error('Invalid plan detail id');
};

export const buildUpdatePlanRequest = (data: EditSubscriptionFormData): UpdatePlanRequest => {
  const planDetails: PlanDetailUpdate[] = data.variants.map((variant) => {
    const planDetailId = toPlanDetailId(variant.planDetailId);
    const pricingRow = data.pricingGrid.find((row) => toPlanDetailId(row.planDetailId) === planDetailId);
    const price = pricingRow
      ? mapRegionalMapToApiPrice(pricingRowToRegionalMap(pricingRow))
      : mapRegionalMapToApiPrice(emptyRegionalPriceMap());
    const variantMeta = variant;

    return {
      planDetailId,
      price,
      connects: variantMeta.connects,
      advanceSearch: variantMeta.advanceSearch,
      limitedSupport: variantMeta.limitedSupport,
      limitedChat: variantMeta.limitedChat,
      limitedChatDays: variantMeta.limitedChatDays,
      highlightedProfile: variantMeta.highlightedProfile,
      astroMatchmaking: variantMeta.astroMatchmaking,
      dedicatedManager: variantMeta.dedicatedManager,
      dedicatedSeniorManager: variantMeta.dedicatedSeniorManager,
      arrangedMeetings: variantMeta.arrangedMeetings,
      hideProfile: variantMeta.hideProfile,
    };
  });

  const isDynamic = data.isDynamic ?? false;

  return {
    name: isDynamic ? CUSTOM_PLAN_NAME : data.name.trim(),
    description: data.description.trim(),
    is_hidden: isDynamic ? true : !data.isActive,
    is_free: isDynamic ? false : data.isFree,
    is_popular: data.isPopular,
    planDetails,
  };
};

export const applyEditFormToPlan = (
  plan: SubscriptionPlanView,
  data: EditSubscriptionFormData
): SubscriptionPlanView => {
  const monthlyRow = data.pricingGrid.find((row) => row.durationMonths === 1);
  const yearlyRow = data.pricingGrid.find((row) => row.durationMonths >= 12);

  const monthlyVariant = data.variants.find((variant) => variant.durationMonths === 1) ?? data.variants[0];
  const isDynamic = Boolean(plan.isDynamic || data.isDynamic);

  return {
    ...plan,
    name: isDynamic ? CUSTOM_PLAN_NAME : data.name.trim(),
    description: data.description.trim(),
    pricingGrid: data.pricingGrid.map((row) => ({
      ...row,
      INR: { ...row.INR },
      USD: { ...row.USD },
      CAD: { ...row.CAD },
      EUR: { ...row.EUR },
    })),
    monthlyPrice: monthlyRow ? pricingRowToRegionalMap(monthlyRow) : plan.monthlyPrice,
    yearlyPrice: yearlyRow ? pricingRowToRegionalMap(yearlyRow) : plan.yearlyPrice,
    features: monthlyVariant ? buildFeatureLabelsFromVariantMeta(monthlyVariant) : [],
    isPopular: data.isPopular,
    isHidden: isDynamic ? true : !data.isActive,
    isFree: isDynamic ? false : data.isFree,
    isDynamic,
  };
};

export const mapApiPlansToSubscription = (
  plans: AdminSubscriptionPlan[]
): SubscriptionPlanView[] =>
  [...plans]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((plan) => ({
      id: plan.planId,
      name: plan.name,
      description: plan.description,
      monthlyPrice: plan.monthlyPrice,
      yearlyPrice: plan.yearlyPrice,
      pricingGrid: buildPricingGrid(plan.monthlyPrice, plan.yearlyPrice),
      features: [],
      subscribers: plan.activeUserCount,
      isHidden: plan.isHidden,
      isPopular: plan.isPopular,
      isFree: plan.isFree,
      isDynamic: plan.isDynamic ?? false,
    }));

export { PLAN_CURRENCIES };
